// v1.1.0: Slack-style chat client — channels, DMs, threads, reactions, pins, saved, mentions,
// unread counts, typing, formatting, autocomplete, search. Renders the full "Chat" view and the
// compact chat in the office sidebar from the same state.
import type { AvatarConfig, ChatChannel, ChatEvent, ChatMessage, NearbyMsg, UserStatus } from '../src/protocol';
import type { Net } from './net';

export interface ChatUser { id: string; name: string; avatar: AvatarConfig; status?: UserStatus }
export interface ChatDeps {
  me: ChatUser;
  net: Net;
  users: () => Map<string, ChatUser>;
  isOnline: (id: string) => boolean;
  toast: (s: string) => void;
  /** total unread + mention counts changed (for the Chat tab badge / title) */
  onUnread?: (unread: number, mentions: number) => void;
  /** v1.3.0: someone's status changed (the office sidebar shows it too) */
  onStatus?: (userId: string, status: UserStatus) => void;
  /** v1.20.0: Nearby chat — send to the people you can hear right now, and how many that is */
  sendNearby?: (text: string) => void;
  nearbyCount?: () => number;
}

const NEARBY = '__nearby';
const NEARBY_KEEP = 100;

type ListView = 'channel' | 'threads' | 'mentions' | 'saved' | 'search';
type Panel = { kind: 'thread'; rootId: string } | { kind: 'details' } | null;

const $ = <T extends HTMLElement>(sel: string, root: ParentNode = document): T => root.querySelector(sel) as T;
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const QUICK_EMOJI = ['👍', '❤️', '😂', '🎉', '👀', '🙏', '🔥', '✅', '😮', '😢', '👏', '🚀', '💯', '🤔', '🦦'];
const SHORTCODES: Record<string, string> = {
  smile: '😄', grin: '😁', joy: '😂', laughing: '😆', wink: '😉', heart: '❤️', thumbsup: '👍', '+1': '👍', thumbsdown: '👎', '-1': '👎', tada: '🎉', fire: '🔥',
  eyes: '👀', pray: '🙏', clap: '👏', rocket: '🚀', '100': '💯', thinking: '🤔', sob: '😭', cry: '😢', wave: '👋', ok: '👌', check: '✅', white_check_mark: '✅', x: '❌',
  warning: '⚠️', star: '⭐', coffee: '☕', pizza: '🍕', otter: '🦦', sparkles: '✨', muscle: '💪', raised_hands: '🙌', shrug: '🤷', facepalm: '🤦', sunglasses: '😎',
  sweat_smile: '😅', rolling_on_the_floor_laughing: '🤣', heart_eyes: '😍', cool: '😎', bulb: '💡', memo: '📝', calendar: '📅', bell: '🔔', zap: '⚡', tea: '🍵',
};

export class ChatApp {
  channels = new Map<string, ChatChannel>();
  private msgs = new Map<string, ChatMessage[]>();
  private hasMore = new Map<string, boolean>();
  private replies = new Map<string, ChatMessage[]>();
  private roots = new Map<string, ChatMessage>();
  current: string | null = null;
  private view: ListView = 'channel';
  private panel: Panel = null;
  private listItems: ChatMessage[] = [];
  private typing = new Map<string, Map<string, { name: string; until: number }>>();
  private editing: string | null = null;
  /** v1.2.0: attachments waiting in the composer, per composer id */
  private pendingFiles = new Map<string, { id: string; name: string; mime: string; size: number; url: string }[]>();
  private atLatest = new Map<string, boolean>();
  /** v1.3.0: how many messages were unread when the channel was opened (for the divider) */
  private dividerAt = new Map<string, number>();
  private uploading = 0;
  private lastTypingSent = 0;
  private readTimer = 0;
  visible = false; // is the full chat view showing?
  /** v1.20.0: the office sidebar chat is showing Nearby instead of a channel */
  miniNearby = false;
  private nearby: NearbyMsg[] = [];
  private nearbyUnread = 0;

  constructor(private deps: ChatDeps) {}

  // ---------- boot ----------

  async load(): Promise<void> {
    const r = await fetch('/api/chat/bootstrap');
    if (!r.ok) { this.deps.toast('Chat failed to load'); return; }
    const data = (await r.json()) as { channels: ChatChannel[] };
    for (const c of data.channels) this.channels.set(c.id, c);
    this.bind();
    try { this.miniNearby = localStorage.getItem('ottsy_mini') === NEARBY; } catch { /* ignore */ }
    if (!this.current) await this.selectChannel('general');
    this.renderSide();
    this.emitUnread();
    window.setInterval(() => this.tickTyping(), 1000);
  }

  private api(path: string, method = 'GET', body?: unknown): Promise<Response> {
    return fetch(`/api/chat/${path}`, { method, headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
  }

  // ---------- helpers ----------

  user(id: string | null): ChatUser | null { return id ? this.deps.users().get(id) ?? null : null; }
  private nameOf(id: string | null): string { if (!id) return 'Unknown'; return this.user(id)?.name ?? (id === this.deps.me.id ? this.deps.me.name : 'Someone'); }
  channelName(c: ChatChannel): string {
    if (c.kind === 'dm' || c.kind === 'group') {
      const others = c.members.filter((m) => m !== this.deps.me.id).map((m) => this.nameOf(m));
      return others.length ? others.join(', ') : this.deps.me.name + ' (you)';
    }
    return c.name;
  }
  statusOf(id: string | null): UserStatus | null { const s = id ? this.user(id)?.status : null; return s && (s.text || s.emoji || s.dndUntil > Date.now()) ? s : null; }
  private statusTag(id: string | null): string { const s = this.statusOf(id); if (!s) return ''; const dnd = s.dndUntil > Date.now(); return `<span class="ustatus" title="${esc(s.text)}">${dnd ? '🔕' : ''}${esc(s.emoji)}</span>`; }
  private myDnd(): boolean { const s = this.deps.users().get(this.deps.me.id)?.status; return Boolean(s && s.dndUntil > Date.now()); }

  private channelIcon(c: ChatChannel): string { return c.kind === 'private' ? '🔒' : c.kind === 'public' ? '#' : c.kind === 'group' ? '👥' : ''; }
  private cur(): ChatChannel | null { return this.current ? this.channels.get(this.current) ?? null : null; }

  private emitUnread(): void {
    let u = 0, m = 0;
    for (const c of this.channels.values()) { u += c.unread; m += c.mentions + (c.kind === 'dm' || c.kind === 'group' ? c.unread : 0); }
    this.deps.onUnread?.(u, m);
  }

  // ---------- data ----------

  async selectChannel(id: string): Promise<void> {
    const c = this.channels.get(id);
    if (!c) return;
    this.current = id; this.view = 'channel'; this.editing = null;
    if (this.panel?.kind === 'thread') this.panel = null;
    this.dividerAt.set(id, c.unread);
    if (!this.msgs.has(id)) await this.fetchMessages(id);
    this.markRead(id);
    this.renderAll();
    this.scrollBottom(true);
  }

  private async fetchMessages(id: string, before?: number, around?: number): Promise<void> {
    const r = await this.api(`channels/${id}/messages?limit=50${before ? `&before=${before}` : ''}${around ? `&around=${around}` : ''}`);
    if (!r.ok) return;
    const data = (await r.json()) as { messages: ChatMessage[]; hasMore: boolean; hasNewer: boolean };
    const existing = this.msgs.get(id) ?? [];
    const merged = before ? [...data.messages, ...existing] : data.messages;
    this.msgs.set(id, dedupe(merged));
    this.hasMore.set(id, data.hasMore);
    if (!before) this.atLatest.set(id, !data.hasNewer);
    for (const m of data.messages) if (m.replyCount) this.roots.set(m.id, m);
  }

  /** v1.2.0: #msg=<id> permalinks — open the channel around that message and highlight it. */
  async jumpTo(messageId: string): Promise<void> {
    const r = await this.api(`messages/${messageId}`);
    if (!r.ok) { this.deps.toast("That message isn't available"); return; }
    const { message, channel } = (await r.json()) as { message: ChatMessage; channel: ChatChannel };
    if (!this.channels.has(channel.id)) this.channels.set(channel.id, channel);
    this.current = channel.id; this.view = 'channel'; this.panel = null;
    await this.fetchMessages(channel.id, undefined, message.ts);
    this.renderAll();
    if (message.threadId) await this.openThread(message.threadId);
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`#c-msgs .cm[data-id="${messageId}"], #c-thread .cm[data-id="${messageId}"]`);
      if (el) { el.scrollIntoView({ block: 'center' }); el.classList.add('flash'); setTimeout(() => el.classList.remove('flash'), 2500); }
    });
  }

  handleHash(): void {
    const m = location.hash.match(/#msg=([\w-]+)/);
    if (m) { void this.jumpTo(m[1]); history.replaceState(null, '', location.pathname); }
  }

  // ---------- v1.2.0: attachments ----------

  private async upload(file: File, composer: string): Promise<void> {
    const id = this.current; if (!id) return;
    if (file.size > 25 * 1024 * 1024) { this.deps.toast(`${file.name} is over 25 MB`); return; }
    this.uploading++; this.renderAttach(composer);
    try {
      const r = await fetch(`/api/files?channelId=${encodeURIComponent(id)}`, { method: 'POST', headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name) }, body: file });
      if (!r.ok) { this.deps.toast(((await r.json().catch(() => ({ error: 'upload failed' }))) as { error: string }).error); return; }
      const f = (await r.json()) as { id: string; name: string; mime: string; size: number; url: string };
      const list = this.pendingFiles.get(composer) ?? []; list.push(f); this.pendingFiles.set(composer, list);
    } finally { this.uploading--; this.renderAttach(composer); }
  }

  private renderAttach(composer: string): void {
    const host = document.getElementById(composer === 'c-input' ? 'c-attach' : 'mini-attach'); if (!host) return;
    const list = this.pendingFiles.get(composer) ?? [];
    host.innerHTML = list.map((f, i) => `<span class="chip">${f.mime.startsWith('image/') ? `<img src="${esc(f.url)}" alt="">` : '📎'} ${esc(f.name)} <button data-rm="${i}" title="Remove">✕</button></span>`).join('') + (this.uploading ? '<span class="chip">⏳ uploading…</span>' : '');
    host.classList.toggle('hidden', !list.length && !this.uploading);
    host.querySelectorAll<HTMLElement>('[data-rm]').forEach((b) => (b.onclick = () => { list.splice(Number(b.dataset.rm), 1); this.renderAttach(composer); }));
  }

  private takeFiles(composer: string): { id: string; name: string; mime: string; size: number; url: string }[] {
    const list = this.pendingFiles.get(composer) ?? [];
    this.pendingFiles.set(composer, []); this.renderAttach(composer);
    return list;
  }

  // ---------- v1.2.0: quick switcher (Ctrl+K) ----------

  openSwitcher(): void {
    let dlg = document.getElementById('c-switch') as HTMLDialogElement | null;
    if (!dlg) { dlg = document.createElement('dialog'); dlg.id = 'c-switch'; document.body.appendChild(dlg); }
    dlg.innerHTML = `<div class="card compact"><input id="c-switch-in" placeholder="Jump to a channel or person…" autocomplete="off"><div id="c-switch-list" class="cm-users"></div></div>`;
    const inp = $('#c-switch-in', dlg) as HTMLInputElement, list = $('#c-switch-list', dlg);
    const items = () => {
      const q = inp.value.toLowerCase().replace(/^[#@]/, '');
      const chans = [...this.channels.values()].filter((c) => c.kind === 'public' || c.kind === 'private').map((c) => ({ label: `${this.channelIcon(c)} ${c.name}`, sub: c.topic, go: () => this.selectChannel(c.id), key: c.name }));
      const people = [...this.deps.users().values()].filter((u) => u.id !== this.deps.me.id).map((u) => ({ label: u.name, sub: this.deps.isOnline(u.id) ? '● online' : 'direct message', go: () => this.openDm([u.id]), key: u.name }));
      return [...chans, ...people].filter((it) => !q || it.key.toLowerCase().includes(q)).slice(0, 12);
    };
    let sel = 0;
    const render = () => { const its = items(); list.innerHTML = its.map((it, i) => `<div class="ac-item${i === sel ? ' sel' : ''}" data-i="${i}"><span>${esc(it.label)}</span><span class="small">${esc(it.sub)}</span></div>`).join('') || '<div class="muted">No matches</div>'; };
    inp.addEventListener('input', () => { sel = 0; render(); });
    inp.addEventListener('keydown', (ev) => {
      const its = items();
      if (ev.key === 'ArrowDown') { sel = (sel + 1) % its.length; render(); ev.preventDefault(); }
      else if (ev.key === 'ArrowUp') { sel = (sel - 1 + its.length) % its.length; render(); ev.preventDefault(); }
      else if (ev.key === 'Enter') { ev.preventDefault(); const it = its[sel]; if (it) { dlg!.close(); void it.go(); } }
      else if (ev.key === 'Escape') dlg!.close();
    });
    list.addEventListener('click', (ev) => { const el = (ev.target as HTMLElement).closest<HTMLElement>('[data-i]'); if (el) { dlg!.close(); void items()[Number(el.dataset.i)]?.go(); } });
    render();
    dlg.showModal(); inp.focus();
  }

  private async loadOlder(): Promise<void> {
    const id = this.current; if (!id || !this.hasMore.get(id)) return;
    const first = this.msgs.get(id)?.[0]; if (!first) return;
    const list = $('#c-msgs'); const h = list.scrollHeight;
    await this.fetchMessages(id, first.ts);
    this.renderMsgs();
    list.scrollTop = list.scrollHeight - h;
  }

  private markRead(id: string): void {
    const c = this.channels.get(id); if (!c) return;
    if (c.unread || c.mentions) { c.unread = 0; c.mentions = 0; this.renderSide(); this.emitUnread(); }
    window.clearTimeout(this.readTimer);
    this.readTimer = window.setTimeout(() => { void this.api(`channels/${id}/read`, 'POST', { ts: Date.now() }); }, 400);
  }

  async send(text: string, threadId?: string, composer = 'c-input'): Promise<void> {
    if (composer === 'mini-input' && this.miniNearby) { text = text.trim(); if (text) this.deps.sendNearby?.(text); return; }
    const id = this.current; if (!id) return;
    text = text.trim();
    const files = threadId ? [] : this.takeFiles(composer);
    if (!text && !files.length) return;
    // slash commands
    const cmd = text.match(/^\/(\w+)\s*(.*)$/s);
    if (cmd && !files.length) { if (await this.slash(cmd[1].toLowerCase(), cmd[2], threadId)) return; }
    const r = await this.api('messages', 'POST', { channelId: id, body: text, threadId: threadId ?? null, files });
    if (!r.ok) { const e = (await r.json().catch(() => ({}))) as { error?: string }; this.deps.toast(e.error ?? 'Could not send'); return; }
    if ('Notification' in window && Notification.permission === 'default') void Notification.requestPermission();
  }

  private async slash(cmd: string, arg: string, threadId?: string): Promise<boolean> {
    const c = this.cur(); if (!c) return true;
    switch (cmd) {
      case 'me': await this.send(`_${this.deps.me.name} ${arg}_`, threadId); return true;
      case 'shrug': await this.send(`${arg} ¯\\_(ツ)_/¯`.trim(), threadId); return true;
      case 'topic': await this.api(`channels/${c.id}`, 'PATCH', { topic: arg }); return true;
      case 'leave': await this.leave(c.id); return true;
      case 'invite': { const ids = [...this.deps.users().values()].filter((u) => arg.toLowerCase().includes('@' + u.name.toLowerCase())).map((u) => u.id); if (ids.length) await this.api(`channels/${c.id}/invite`, 'POST', { userIds: ids }); else this.deps.toast('Use /invite @Name'); return true; }
      case 'dm': { const u = [...this.deps.users().values()].find((x) => arg.toLowerCase().startsWith('@' + x.name.toLowerCase())); if (u) await this.openDm([u.id]); else this.deps.toast('Use /dm @Name'); return true; }
      case 'knock': { const u = [...this.deps.users().values()].find((x) => arg.toLowerCase().includes('@' + x.name.toLowerCase())); if (u) { await fetch('/api/knock', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: u.id }) }); this.deps.toast(`Knocked for ${u.name} on Slack`); } return true; }
      case 'meet': { window.open('https://meet.google.com/new', '_blank', 'noopener'); return true; }
      case 'status': { const m = arg.match(/^(\p{Extended_Pictographic}\S*)?\s*(.*)$/u); await fetch('/api/me/status', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emoji: m?.[1] ?? '', text: m?.[2] ?? arg }) }); this.deps.toast(arg ? 'Status set' : 'Status cleared'); return true; }
      case 'dnd': { const mins = /^(\d+)\s*h/i.test(arg) ? Number(arg.match(/^(\d+)/)![1]) * 60 : /^(\d+)/.test(arg) ? Number(arg.match(/^(\d+)/)![1]) : arg.toLowerCase().startsWith('off') ? 0 : 60; await fetch('/api/me/status', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dndMinutes: mins }) }); this.deps.toast(mins ? `Do not disturb for ${mins >= 60 ? `${mins / 60}h` : `${mins}m`}` : 'Notifications back on'); return true; }
      case 'help': this.deps.toast('/me /shrug /topic /invite @x /dm @x /knock @x /meet /status 🏖 text /dnd 1h|off — Enter sends, Shift+Enter newline, ↑ edits your last message'); return true;
      default: return false;
    }
  }

  async openDm(userIds: string[]): Promise<void> {
    const r = await this.api('dm', 'POST', { userIds });
    if (!r.ok) { this.deps.toast('Could not open DM'); return; }
    const { channel } = (await r.json()) as { channel: ChatChannel };
    this.channels.set(channel.id, channel);
    await this.selectChannel(channel.id);
  }

  private async leave(id: string): Promise<void> {
    const r = await this.api(`channels/${id}/leave`, 'POST');
    if (!r.ok) { const e = (await r.json().catch(() => ({}))) as { error?: string }; this.deps.toast(e.error ?? 'Could not leave'); return; }
    this.channels.delete(id); this.msgs.delete(id);
    await this.selectChannel('general');
  }

  async react(messageId: string, emoji: string): Promise<void> { await this.api(`messages/${messageId}/reactions`, 'POST', { emoji }); }

  private find(messageId: string): ChatMessage | null {
    for (const list of this.msgs.values()) { const m = list.find((x) => x.id === messageId); if (m) return m; }
    for (const list of this.replies.values()) { const m = list.find((x) => x.id === messageId); if (m) return m; }
    return this.roots.get(messageId) ?? this.listItems.find((x) => x.id === messageId) ?? null;
  }

  async openThread(rootId: string): Promise<void> {
    const r = await this.api(`threads/${rootId}`);
    if (!r.ok) return;
    const data = (await r.json()) as { root: ChatMessage; replies: ChatMessage[] };
    this.roots.set(rootId, data.root);
    this.replies.set(rootId, data.replies);
    this.panel = { kind: 'thread', rootId };
    if (!this.visible) { this.deps.toast('Open the Chat tab to view threads'); }
    this.renderPanel();
  }

  // ---------- events from the server ----------

  onEvent(ev: ChatEvent): void {
    switch (ev.type) {
      case 'message': {
        const m = ev.message;
        const c = this.channels.get(m.channelId);
        if (!c) { void this.refreshChannel(m.channelId); return; }
        c.lastTs = Math.max(c.lastTs, m.ts);
        if (m.threadId) {
          const list = this.replies.get(m.threadId);
          if (list) this.replies.set(m.threadId, upsert(list, m));
          const root = this.find(m.threadId);
          if (root && !ev.edit) { root.replyCount = (root.replyCount ?? 0) + 1; root.lastReplyTs = m.ts; }
        } else {
          const list = this.msgs.get(m.channelId);
          if (list) this.msgs.set(m.channelId, upsert(list, m));
        }
        const mine = m.userId === this.deps.me.id;
        const viewing = this.visible && this.current === m.channelId && this.view === 'channel' && document.visibilityState === 'visible';
        const inThread = this.panel?.kind === 'thread' && this.panel.rootId === m.threadId;
        if (!mine && !ev.edit && m.kind !== 'system') {
          const mentioned = m.meta.mentions?.includes(this.deps.me.id) || m.meta.mentions?.includes('*');
          if (!viewing && !m.threadId) { c.unread++; if (mentioned) c.mentions++; }
          if ((!viewing || document.visibilityState !== 'visible') && (!c.muted || mentioned)) this.notify(c, m, Boolean(mentioned));
          this.typing.get(m.channelId)?.delete(m.userId ?? '');
        }
        if (viewing && !m.threadId) this.markRead(m.channelId);
        if (this.current === m.channelId) { this.renderMsgs(); this.renderMini(); this.scrollBottom(false); }
        if (inThread) this.renderPanel();
        this.renderSide(); this.emitUnread();
        break;
      }
      case 'delete': {
        const list = ev.threadId ? this.replies.get(ev.threadId) : this.msgs.get(ev.channelId);
        const m = list?.find((x) => x.id === ev.messageId);
        if (m) { m.deleted = true; m.body = ''; }
        if (ev.threadId) { const root = this.find(ev.threadId); if (root) root.replyCount = Math.max(0, root.replyCount - 1); }
        this.renderMsgs(); this.renderMini(); this.renderPanel();
        break;
      }
      case 'reaction': {
        const m = this.find(ev.messageId);
        if (m) {
          let r = m.reactions.find((x) => x.emoji === ev.emoji);
          if (ev.on) { if (!r) { r = { emoji: ev.emoji, users: [] }; m.reactions.push(r); } if (!r.users.includes(ev.userId)) r.users.push(ev.userId); }
          else if (r) { r.users = r.users.filter((u) => u !== ev.userId); if (!r.users.length) m.reactions = m.reactions.filter((x) => x !== r); }
        }
        this.renderMsgs(); this.renderMini(); this.renderPanel();
        break;
      }
      case 'pin': { const m = this.find(ev.messageId); if (m) m.pinned = ev.on; this.renderMsgs(); this.renderHead(); break; }
      case 'channel': {
        const prev = this.channels.get(ev.channel.id);
        if (prev && !ev.channel.members.includes(this.deps.me.id)) break; // a public channel I'm not in — keep for browse only
        if (!prev && ev.channel.kind === 'public' && !ev.channel.members.includes(this.deps.me.id)) break;
        this.channels.set(ev.channel.id, prev ? { ...ev.channel, unread: prev.unread, mentions: prev.mentions } : ev.channel);
        this.renderSide(); this.renderHead(); this.emitUnread();
        break;
      }
      case 'membership': { void this.refreshChannel(ev.channelId); break; }
      case 'status': { this.deps.onStatus?.(ev.userId, ev.status); this.renderSide(); this.renderHead(); this.renderPanel(); break; }
      case 'typing': {
        const map = this.typing.get(ev.channelId) ?? new Map();
        map.set(ev.userId, { name: ev.name, until: Date.now() + 4000 });
        this.typing.set(ev.channelId, map);
        this.renderTyping();
        break;
      }
    }
  }

  private async refreshChannel(id: string): Promise<void> {
    const r = await this.api('bootstrap');
    if (!r.ok) return;
    const data = (await r.json()) as { channels: ChatChannel[] };
    const fresh = data.channels.find((c) => c.id === id);
    if (fresh) { const prev = this.channels.get(id); this.channels.set(id, prev ? { ...fresh, unread: prev.unread, mentions: prev.mentions } : fresh); }
    else { this.channels.delete(id); if (this.current === id) await this.selectChannel('general'); }
    this.renderSide(); this.renderHead();
  }

  private notify(c: ChatChannel, m: ChatMessage, mentioned: boolean): void {
    const direct = c.kind === 'dm' || c.kind === 'group';
    if (!direct && !mentioned) return;
    if (this.myDnd()) return;
    if (localStorage.getItem('ottsy.sound') !== 'off') beep();
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const who = m.kind === 'webhook' ? (m.meta.name ?? 'Integration') : this.nameOf(m.userId);
    try {
      const n = new Notification(direct ? who : `${who} in #${c.name}`, { body: m.body.slice(0, 140), tag: m.id, silent: false });
      n.onclick = () => { window.focus(); void this.selectChannel(c.id); n.close(); };
    } catch { /* ignore */ }
  }

  private tickTyping(): void {
    const now = Date.now(); let changed = false;
    for (const map of this.typing.values()) for (const [k, v] of map) if (v.until < now) { map.delete(k); changed = true; }
    if (changed) this.renderTyping();
  }

  private sendTyping(): void {
    const now = Date.now();
    if (!this.current || now - this.lastTypingSent < 2500) return;
    this.lastTypingSent = now;
    this.deps.net.send({ t: 'typing', channelId: this.current });
  }

  // ---------- formatting ----------

  fmt(body: string): string {
    let s = esc(body);
    const users = [...this.deps.users().values()];
    // code blocks first so nothing inside them is formatted
    const blocks: string[] = [];
    s = s.replace(/```([\s\S]*?)```/g, (_m, code: string) => { blocks.push(`<pre class="code">${code.replace(/^\n|\n$/g, '')}</pre>`); return `\u0000${blocks.length - 1}\u0000`; });
    s = s.replace(/`([^`\n]+)`/g, '<code>$1</code>');
    s = s.replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>').replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,!?]|$)/g, '$1<b>$2</b>');
    s = s.replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,!?]|$)/g, '$1<i>$2</i>');
    s = s.replace(/~([^~\n]+)~/g, '<s>$1</s>');
    s = s.replace(/(https?:\/\/[^\s<]+[^\s<.,;:!?)])/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
    s = s.replace(/:([a-z0-9_+-]+):/gi, (m, code: string) => SHORTCODES[code.toLowerCase()] ?? m);
    s = s.replace(/@(channel|here|everyone)\b/gi, '<span class="mention all">@$1</span>');
    for (const u of users.sort((a, b) => b.name.length - a.name.length)) {
      const re = new RegExp(`@${u.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      s = s.replace(re, `<span class="mention${u.id === this.deps.me.id ? ' me' : ''}" data-user="${u.id}">@${esc(u.name)}</span>`);
    }
    s = s.replace(/(^|\s)#([a-z0-9_-]{2,})/gi, (m, pre: string, name: string) => { const c = [...this.channels.values()].find((x) => x.name === name.toLowerCase()); return c ? `${pre}<a class="chanref" data-chan="${c.id}">#${esc(name)}</a>` : m; });
    s = s.split('\n').map((line) => (line.startsWith('&gt; ') ? `<blockquote>${line.slice(5)}</blockquote>` : line.startsWith('- ') || line.startsWith('• ') ? `<div class="li">• ${line.slice(2)}</div>` : line)).join('\n');
    s = s.replace(/\n/g, '<br>').replace(/<\/blockquote><br>/g, '</blockquote>').replace(/<\/div><br>/g, '</div>');
    s = s.replace(/\u0000(\d+)\u0000/g, (_m, i: string) => blocks[Number(i)]);
    return s;
  }

  private avatarHtml(u: ChatUser | null, kind: ChatMessage['kind'], meta: ChatMessage['meta']): string {
    if (kind === 'webhook') return `<span class="cav hook">${esc(meta.icon ?? '🔔')}</span>`;
    const color = u?.avatar.color || '#555', ini = u?.avatar.initials || (u?.name ?? '?').slice(0, 2).toUpperCase();
    return `<span class="cav" style="background:${esc(color)}">${esc(ini)}</span>`;
  }

  private fmtTime(ts: number): string { return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  private dayLabel(ts: number): string {
    const d = new Date(ts), t = new Date(); t.setHours(0, 0, 0, 0);
    const day = new Date(d); day.setHours(0, 0, 0, 0);
    const diff = Math.round((t.getTime() - day.getTime()) / 86400000);
    if (diff === 0) return 'Today'; if (diff === 1) return 'Yesterday';
    if (diff < 7) return d.toLocaleDateString([], { weekday: 'long' });
    return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: d.getFullYear() === t.getFullYear() ? undefined : 'numeric' });
  }

  private msgHtml(m: ChatMessage, opts: { compact?: boolean; grouped?: boolean; showChannel?: boolean; inThread?: boolean } = {}): string {
    const u = this.user(m.userId);
    const mine = m.userId === this.deps.me.id;
    const who = m.kind === 'webhook' ? (m.meta.name ?? 'Integration') : this.nameOf(m.userId);
    if (m.kind === 'system') return `<div class="cm sys" data-id="${m.id}"><span>${esc(m.body)}</span><span class="t">${this.fmtTime(m.ts)}</span></div>`;
    const chan = this.channels.get(m.channelId);
    const files = (m.meta.files ?? []).map((f) => f.mime.startsWith('image/')
      ? `<a class="cfile img" href="${esc(f.url)}" target="_blank" rel="noopener"><img src="${esc(f.url)}" alt="${esc(f.name)}" loading="lazy"></a>`
      : `<a class="cfile" href="${esc(f.url)}" target="_blank" rel="noopener">📎 ${esc(f.name)} <span class="small">${fmtSize(f.size)}</span></a>`).join('');
    const reactions = m.reactions.length ? `<div class="creacts">${m.reactions.map((r) => `<button class="creact${r.users.includes(this.deps.me.id) ? ' on' : ''}" data-act="react" data-emoji="${esc(r.emoji)}" title="${esc(r.users.map((x) => this.nameOf(x)).join(', '))}">${esc(r.emoji)} ${r.users.length}</button>`).join('')}<button class="creact add" data-act="pick" title="Add reaction">😀+</button></div>` : '';
    const thread = !opts.inThread && m.replyCount ? `<button class="cthread" data-act="thread">💬 ${m.replyCount} ${m.replyCount === 1 ? 'reply' : 'replies'} · last ${this.fmtTime(m.lastReplyTs ?? m.ts)}</button>` : '';
    const body = m.deleted ? `<span class="deleted">This message was deleted.</span>` : this.editing === m.id
      ? `<div class="cedit"><textarea class="cedit-in">${esc(m.body)}</textarea><div class="row"><button class="btn small-btn" data-act="edit-cancel">Cancel</button><button class="btn small-btn primary" data-act="edit-save">Save</button></div></div>`
      : `<div class="cbody">${this.fmt(m.body)}${m.editedAt ? ' <span class="edited">(edited)</span>' : ''}</div>${files}`;
    const tools = m.deleted || opts.compact ? '' : `<div class="ctools">
      ${QUICK_EMOJI.slice(0, 3).map((e) => `<button data-act="react" data-emoji="${e}" title="React ${e}">${e}</button>`).join('')}
      <button data-act="pick" title="Add reaction">😀</button>
      ${opts.inThread ? '' : '<button data-act="thread" title="Reply in thread">💬</button>'}
      <button data-act="save" title="${m.saved ? 'Unsave' : 'Save for later'}">${m.saved ? '🔖' : '📑'}</button>
      <button data-act="pin" title="${m.pinned ? 'Unpin' : 'Pin to channel'}">📌</button>
      <button data-act="link" title="Copy link">🔗</button>
      ${mine ? '<button data-act="edit" title="Edit">✏️</button><button data-act="del" title="Delete">🗑️</button>' : ''}
    </div>`;
    const chanTag = opts.showChannel && chan ? `<a class="chanref small" data-chan="${chan.id}">${this.channelIcon(chan)}${esc(this.channelName(chan))}</a> · ` : '';
    return `<div class="cm${opts.grouped ? ' grouped' : ''}${m.pinned ? ' pinned' : ''}${mine ? ' mine' : ''}" data-id="${m.id}" data-chan="${m.channelId}">
      ${opts.grouped ? `<span class="t hover">${this.fmtTime(m.ts)}</span>` : this.avatarHtml(u, m.kind, m.meta)}
      <div class="cmain">
        ${opts.grouped ? '' : `<div class="chead">${chanTag}<b class="cname" data-user="${esc(m.userId ?? '')}">${esc(who)}</b>${m.kind === 'webhook' ? '<span class="app">APP</span>' : ''}<span class="t">${this.fmtTime(m.ts)}</span>${m.pinned ? '<span class="small">📌 pinned</span>' : ''}</div>`}
        ${body}${reactions}${thread}
      </div>${tools}</div>`;
  }

  private listHtml(list: ChatMessage[], opts: { compact?: boolean; showChannel?: boolean; inThread?: boolean; divider?: number } = {}): string {
    let out = ''; let lastDay = ''; let prev: ChatMessage | null = null;
    const dividerIdx = opts.divider && opts.divider > 0 && opts.divider <= list.length ? list.length - opts.divider : -1;
    list.forEach((m, i) => {
      if (i === dividerIdx) { out += '<div class="cday new"><span>New messages</span></div>'; prev = null; }
      const day = this.dayLabel(m.ts);
      if (day !== lastDay && !opts.inThread) { out += `<div class="cday"><span>${day}</span></div>`; lastDay = day; prev = null; }
      const grouped = Boolean(prev && prev.userId === m.userId && prev.kind === m.kind && m.kind !== 'system' && m.ts - prev.ts < 5 * 60_000 && !prev.pinned && !opts.showChannel);
      out += this.msgHtml(m, { ...opts, grouped });
      prev = m;
    });
    return out || `<div class="cempty">${opts.inThread ? 'No replies yet — start the thread.' : 'No messages yet. Say hi 👋'}</div>`;
  }

  // ---------- rendering ----------

  private renderAll(): void { this.renderSide(); this.renderHead(); this.renderMsgs(); this.renderPanel(); this.renderMini(); this.renderTyping(); }

  renderSide(): void {
    const side = document.getElementById('c-chan-list'); if (!side) return;
    const all = [...this.channels.values()];
    const chans = all.filter((c) => c.kind === 'public' || c.kind === 'private').sort((a, b) => a.name.localeCompare(b.name));
    const dms = all.filter((c) => c.kind === 'dm' || c.kind === 'group').sort((a, b) => b.lastTs - a.lastTs);
    const item = (c: ChatChannel) => {
      const name = this.channelName(c);
      const other = c.kind === 'dm' ? c.members.find((m) => m !== this.deps.me.id) ?? this.deps.me.id : null;
      const dot = other ? `<span class="pres${this.deps.isOnline(other) ? ' on' : ''}"></span>` : `<span class="cic">${this.channelIcon(c)}</span>`;
      const badge = c.mentions || (c.unread && (c.kind === 'dm' || c.kind === 'group')) ? `<span class="badge">${c.mentions || c.unread}</span>` : '';
      return `<button class="citem${c.id === this.current && this.view === 'channel' ? ' sel' : ''}${c.unread && !c.muted ? ' unread' : ''}${c.archived || c.muted ? ' archived' : ''}" data-chan="${c.id}" title="${c.muted ? 'Muted' : esc(this.statusOf(other)?.text ?? '')}">${dot}<span class="cn">${esc(name)}${c.muted ? ' 🔕' : ''}</span>${other ? this.statusTag(other) : ''}${badge}</button>`;
    };
    side.innerHTML = `
      <div class="csec"><button class="citem nav${this.view === 'threads' ? ' sel' : ''}" data-view="threads">🧵 Threads</button>
      <button class="citem nav${this.view === 'mentions' ? ' sel' : ''}" data-view="mentions">@ Mentions</button>
      <button class="citem nav${this.view === 'saved' ? ' sel' : ''}" data-view="saved">🔖 Saved</button></div>
      <div class="csec"><div class="csec-h"><span>Channels</span><button class="cplus" data-act="new-channel" title="Create or browse channels">+</button></div>${chans.map(item).join('')}</div>
      <div class="csec"><div class="csec-h"><span>Direct messages</span><button class="cplus" data-act="new-dm" title="New message">+</button></div>${dms.map(item).join('')}</div>`;
    const mini = document.getElementById('mini-chan') as HTMLSelectElement | null;
    if (mini) {
      mini.innerHTML = `<option value="${NEARBY}"${this.miniNearby ? ' selected' : ''}>📍 Nearby${this.nearbyUnread ? ` (${this.nearbyUnread})` : ''}</option>` + [...chans, ...dms].map((c) => `<option value="${c.id}"${c.id === this.current && !this.miniNearby ? ' selected' : ''}>${esc((this.channelIcon(c) ? this.channelIcon(c) + ' ' : '') + this.channelName(c))}${c.unread ? ` (${c.unread})` : ''}</option>`).join('');
    }
  }

  renderHead(): void {
    const head = document.getElementById('c-head'); if (!head) return;
    const c = this.cur();
    if (this.view !== 'channel' || !c) {
      const titles: Record<ListView, string> = { channel: '', threads: '🧵 Threads', mentions: '@ Mentions & reactions', saved: '🔖 Saved items', search: '🔍 Search results' };
      head.innerHTML = `<div class="chtitle">${titles[this.view]}</div>`;
      return;
    }
    const memberNames = c.members.map((m) => this.nameOf(m));
    const other = c.kind === 'dm' ? c.members.find((m) => m !== this.deps.me.id) ?? null : null;
    const st = other ? this.statusOf(other) : null;
    head.innerHTML = `<div class="chtitle"><span class="cic">${this.channelIcon(c)}</span>${other ? `<span class="pres${this.deps.isOnline(other) ? ' on' : ''}"></span>` : ''}${esc(this.channelName(c))}${c.archived ? ' <span class="small">(archived)</span>' : ''}${st ? ` <span class="ustatus big" title="${esc(st.text)}">${st.dndUntil > Date.now() ? '🔕 ' : ''}${esc(st.emoji)} ${esc(st.text)}</span>` : ''}</div>
      <div class="chtopic" data-act="topic" title="Click to set the topic">${c.topic ? esc(c.topic) : '<span class="muted">Add a topic</span>'}</div>
      <div class="chtools">
        <button class="ctl" data-act="members" title="${esc(memberNames.join(', '))}">👥 ${c.members.length}</button>
        <button class="ctl" data-act="pins" title="Pinned messages">📌</button>
        <button class="ctl" data-act="meet" title="Start a Google Meet">📹</button>
        <button class="ctl" data-act="details" title="Channel details">ⓘ</button>
      </div>`;
  }

  renderMsgs(): void {
    const el = document.getElementById('c-msgs'); if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (this.view === 'channel') {
      const id = this.current; if (!id) { el.innerHTML = ''; return; }
      const list = this.msgs.get(id) ?? [];
      el.innerHTML = (this.hasMore.get(id) ? '<button class="cmore" data-act="more">Load older messages</button>' : '<div class="cstart">This is the very beginning of the conversation.</div>') + this.listHtml(list, { divider: this.dividerAt.get(id) })
        + (this.atLatest.get(id) === false ? '<button class="cjump" data-act="latest">↓ Jump to latest</button>' : '');
    } else {
      el.innerHTML = this.listHtml(this.listItems, { showChannel: true, inThread: this.view === 'threads' });
    }
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }

  renderPanel(): void {
    const panel = document.getElementById('c-panel'); if (!panel) return;
    if (!this.panel) { panel.classList.add('hidden'); return; }
    panel.classList.remove('hidden');
    if (this.panel.kind === 'thread') {
      const root = this.roots.get(this.panel.rootId) ?? this.find(this.panel.rootId);
      const replies = this.replies.get(this.panel.rootId) ?? [];
      panel.innerHTML = `<div class="cp-h"><b>Thread</b><span class="small">${root ? esc(this.channelName(this.channels.get(root.channelId)!)) : ''}</span><button class="cplus" data-act="close-panel">✕</button></div>
        <div class="cp-body" id="c-thread">${root ? this.msgHtml(root, { inThread: true }) : ''}<div class="cday"><span>${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}</span></div>${this.listHtml(replies, { inThread: true })}</div>
        <div class="ccomp"><textarea id="c-thread-in" placeholder="Reply…" rows="1"></textarea><button class="btn small-btn primary" data-act="thread-send">Send</button></div>`;
      const body = $('#c-thread', panel); body.scrollTop = body.scrollHeight;
    } else {
      const c = this.cur(); if (!c) return;
      const mem = c.members.map((id) => `<div class="cmember"><span class="pres${this.deps.isOnline(id) ? ' on' : ''}"></span>${esc(this.nameOf(id))}${id === this.deps.me.id ? ' (you)' : ''}${this.statusTag(id)}<span class="small">${esc(this.statusOf(id)?.text ?? '')}</span></div>`).join('');
      panel.innerHTML = `<div class="cp-h"><b>${this.channelIcon(c)} ${esc(this.channelName(c))}</b><button class="cplus" data-act="close-panel">✕</button></div>
        <div class="cp-body">
          <div class="cp-sec"><div class="cp-l">Topic</div><div>${c.topic ? esc(c.topic) : '<span class="muted">None</span>'}</div><button class="btn small-btn" data-act="topic">Edit</button></div>
          <div class="cp-sec"><div class="cp-l">Purpose</div><div>${c.purpose ? esc(c.purpose) : '<span class="muted">None</span>'}</div><button class="btn small-btn" data-act="purpose">Edit</button></div>
          <div class="cp-sec"><div class="cp-l">Members · ${c.members.length}</div>${mem}${c.kind !== 'dm' ? '<button class="btn small-btn" data-act="add-members">Add people</button>' : ''}</div>
          <div class="cp-sec row"><button class="btn small-btn" data-act="files">📎 Files</button><button class="btn small-btn" data-act="pins">📌 Pins</button><button class="btn small-btn" data-act="mute">${c.muted ? '🔔 Unmute' : '🔕 Mute'}</button></div>
          ${c.kind === 'public' || c.kind === 'private' ? `<div class="cp-sec"><div class="cp-l">Integrations</div><div class="small">Incoming webhooks post messages into this channel from other tools.</div><button class="btn small-btn" data-act="webhooks">Manage webhooks</button></div>` : ''}
          <div class="cp-sec row">
            ${c.kind === 'public' || c.kind === 'private' ? `<button class="btn small-btn" data-act="rename">Rename</button><button class="btn small-btn" data-act="archive">${c.archived ? 'Unarchive' : 'Archive'}</button>` : ''}
            ${c.id !== 'general' ? '<button class="btn small-btn danger" data-act="leave">Leave</button>' : ''}
          </div>
        </div>`;
    }
  }

  renderMini(): void {
    const el = document.getElementById('mini-msgs'); if (!el) return;
    document.getElementById('sidebar')?.classList.toggle('nearby', this.miniNearby);
    if (this.miniNearby) { this.renderNearby(el); return; }
    (document.getElementById('mini-input') as HTMLTextAreaElement | null)?.setAttribute('placeholder', 'Message…  (Enter to send)');
    const id = this.current; if (!id) return;
    const list = (this.msgs.get(id) ?? []).slice(-60);
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    el.innerHTML = this.listHtml(list, { compact: true });
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }

  // ---------- v1.20.0: Nearby chat ----------

  /** a Nearby message arrived (the server only sends the ones meant for me) */
  onNearby(m: NearbyMsg): void {
    this.nearby.push(m);
    if (this.nearby.length > NEARBY_KEEP) this.nearby.splice(0, this.nearby.length - NEARBY_KEEP);
    if (!this.miniNearby && m.userId !== this.deps.me.id) { this.nearbyUnread++; this.renderSide(); }
    if (this.miniNearby) this.renderMini();
  }

  private setMiniNearby(on: boolean): void {
    this.miniNearby = on;
    if (on) this.nearbyUnread = 0;
    try { localStorage.setItem('ottsy_mini', on ? NEARBY : 'channel'); } catch { /* ignore */ }
    this.renderSide(); this.renderMini();
    const el = document.getElementById('mini-msgs'); if (el) el.scrollTop = el.scrollHeight;
  }

  /** who's in range may have changed: refresh the composer hint */
  refreshNearby(): void {
    if (!this.miniNearby) return;
    const n = this.deps.nearbyCount?.() ?? 0;
    const ph = n ? `Message the ${n === 1 ? 'otter' : `${n} otters`} near you…` : 'Nobody near you yet: walk up to someone';
    const ta = document.getElementById('mini-input') as HTMLTextAreaElement | null;
    if (ta && ta.placeholder !== ph) ta.placeholder = ph;
  }

  private renderNearby(el: HTMLElement): void {
    this.refreshNearby();
    const me = this.deps.me.id;
    const rows = this.nearby.map((m) => {
      const mine = m.userId === me;
      const heard = mine ? `<span class="nb-to">${m.to.length ? `heard by ${esc(m.to.map((id) => this.nameOf(id)).join(', '))}` : 'nobody was near'}</span>` : '';
      return `<div class="cm nb${mine ? ' mine' : ''}">${this.avatarHtml(this.user(m.userId), 'user', {})}<div class="cmain"><div class="chead"><b class="cname" data-user="${esc(m.userId)}">${esc(mine ? 'You' : m.name)}</b><span class="t">${this.fmtTime(m.ts)}</span></div><div class="cbody">${this.fmt(m.body)}</div>${heard}</div></div>`;
    }).join('');
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    el.innerHTML = `<div class="nb-hint">📍 Goes to the people you can hear right now (in your room, or close by). Not saved.</div>${rows || '<div class="cempty">No nearby messages yet.</div>'}`;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }

  renderTyping(): void {
    const el = document.getElementById('c-typing'); if (!el) return;
    const names = [...(this.typing.get(this.current ?? '')?.values() ?? [])].map((t) => t.name);
    el.textContent = names.length ? `${names.slice(0, 3).join(', ')} ${names.length === 1 ? 'is' : 'are'} typing…` : '';
  }

  private scrollBottom(force: boolean): void {
    for (const id of ['c-msgs', 'mini-msgs']) { const el = document.getElementById(id); if (!el) continue; if (force || el.scrollHeight - el.scrollTop - el.clientHeight < 120) el.scrollTop = el.scrollHeight; }
  }

  // ---------- list views ----------

  private async showList(view: ListView, q?: string): Promise<void> {
    this.view = view; this.panel = null;
    const path = view === 'search' ? `search?q=${encodeURIComponent(q ?? '')}` : view;
    const r = await this.api(path);
    this.listItems = r.ok ? ((await r.json()) as { messages: ChatMessage[] }).messages : [];
    for (const m of this.listItems) if (m.replyCount) this.roots.set(m.id, m);
    this.renderAll();
  }

  async search(q: string): Promise<void> { if (!q.trim()) return; this.visible = true; await this.showList('search', q); }

  // ---------- DOM wiring ----------

  private bind(): void {
    const root = document.getElementById('chatview'); if (!root) return;
    root.addEventListener('click', (ev) => void this.onClick(ev));
    document.getElementById('sidebar')?.addEventListener('click', (ev) => void this.onClick(ev));
    const input = document.getElementById('c-input') as HTMLTextAreaElement;
    const mini = document.getElementById('mini-input') as HTMLTextAreaElement;
    for (const ta of [input, mini]) {
      ta.addEventListener('keydown', (ev) => this.onComposerKey(ev, ta, null));
      ta.addEventListener('input', () => { autoGrow(ta); if (!(ta === mini && this.miniNearby)) this.sendTyping(); this.autocomplete(ta); });
    }
    root.addEventListener('keydown', (ev) => {
      const t = ev.target as HTMLElement;
      if (t.id === 'c-thread-in' && this.panel?.kind === 'thread') this.onComposerKey(ev, t as HTMLTextAreaElement, this.panel.rootId);
      if (t.classList.contains('cedit-in')) {
        if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); void this.saveEdit(t.closest('.cm')!.getAttribute('data-id')!, (t as HTMLTextAreaElement).value); }
        if (ev.key === 'Escape') { this.editing = null; this.renderMsgs(); this.renderPanel(); }
      }
    });
    root.addEventListener('input', (ev) => { const t = ev.target as HTMLElement; if (t.id === 'c-thread-in') { autoGrow(t as HTMLTextAreaElement); this.sendTyping(); } });
    $('#c-msgs').addEventListener('scroll', () => { const el = $('#c-msgs'); if (el.scrollTop < 40 && this.view === 'channel') void this.loadOlder(); });
    ($('#c-search') as HTMLInputElement).addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { void this.search(($('#c-search') as HTMLInputElement).value); } });
    (document.getElementById('mini-chan') as HTMLSelectElement | null)?.addEventListener('change', (ev) => {
      const v = (ev.target as HTMLSelectElement).value;
      if (v === NEARBY) { this.setMiniNearby(true); return; }
      if (this.miniNearby) this.setMiniNearby(false);
      void this.selectChannel(v);
    });
    // v1.2.0: attachments — button, drag & drop onto the conversation, paste images
    for (const [btnId, inputId, composer] of [['c-attach-btn', 'c-file', 'c-input'], ['mini-attach-btn', 'mini-file', 'mini-input']] as const) {
      const btn = document.getElementById(btnId), fin = document.getElementById(inputId) as HTMLInputElement | null;
      if (!btn || !fin) continue;
      btn.onclick = () => fin.click();
      fin.onchange = () => { for (const f of fin.files ?? []) void this.upload(f, composer); fin.value = ''; };
    }
    for (const [zoneId, composer] of [['c-main', 'c-input'], ['sidebar', 'mini-input']] as const) {
      const zone = document.getElementById(zoneId); if (!zone) continue;
      zone.addEventListener('dragover', (ev) => { if (ev.dataTransfer?.types.includes('Files')) { ev.preventDefault(); zone.classList.add('dropping'); } });
      zone.addEventListener('dragleave', () => zone.classList.remove('dropping'));
      zone.addEventListener('drop', (ev) => { zone.classList.remove('dropping'); if (!ev.dataTransfer?.files.length) return; ev.preventDefault(); for (const f of ev.dataTransfer.files) void this.upload(f, composer); });
    }
    for (const ta of [input, mini]) ta.addEventListener('paste', (ev) => { const files = [...(ev.clipboardData?.files ?? [])]; if (files.length) { ev.preventDefault(); for (const f of files) void this.upload(f, ta.id); } });
    window.addEventListener('hashchange', () => this.handleHash());
    this.handleHash();
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && this.current && this.view === 'channel') this.markRead(this.current); });
  }

  private onComposerKey(ev: KeyboardEvent, ta: HTMLTextAreaElement, threadId: string | null): void {
    const ac = document.getElementById('c-ac');
    if (ac && !ac.classList.contains('hidden') && (ev.key === 'ArrowDown' || ev.key === 'ArrowUp' || ev.key === 'Tab' || (ev.key === 'Enter' && ac.querySelector('.sel')))) {
      ev.preventDefault();
      const items = [...ac.querySelectorAll<HTMLElement>('.ac-item')]; const i = items.findIndex((x) => x.classList.contains('sel'));
      if (ev.key === 'ArrowDown') { items[i]?.classList.remove('sel'); items[(i + 1) % items.length]?.classList.add('sel'); }
      else if (ev.key === 'ArrowUp') { items[i]?.classList.remove('sel'); items[(i - 1 + items.length) % items.length]?.classList.add('sel'); }
      else this.acceptAc(ta, items[i] ?? items[0]);
      return;
    }
    if (ev.key === 'Escape') { ac?.classList.add('hidden'); ta.blur(); return; }
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      const text = ta.value; ta.value = ''; autoGrow(ta); ac?.classList.add('hidden');
      void this.send(text, threadId ?? undefined, ta.id);
      return;
    }
    if (ev.key === 'ArrowUp' && !ta.value && !threadId) {
      const mine = [...(this.msgs.get(this.current ?? '') ?? [])].reverse().find((m) => m.userId === this.deps.me.id && !m.deleted && m.kind === 'user');
      if (mine) { ev.preventDefault(); this.editing = mine.id; this.renderMsgs(); ($('.cedit-in') as HTMLTextAreaElement | null)?.focus(); }
    }
  }

  private autocomplete(ta: HTMLTextAreaElement): void {
    let ac = document.getElementById('c-ac');
    if (!ac) { ac = document.createElement('div'); ac.id = 'c-ac'; ac.className = 'hidden'; document.body.appendChild(ac); ac.addEventListener('mousedown', (ev) => { ev.preventDefault(); const it = (ev.target as HTMLElement).closest<HTMLElement>('.ac-item'); if (it) this.acceptAc(ta, it); }); }
    const upto = ta.value.slice(0, ta.selectionStart ?? ta.value.length);
    const m = upto.match(/(?:^|\s)([@#:])([\w.-]*)$/);
    if (!m) { ac.classList.add('hidden'); return; }
    const kind = m[1], q = m[2].toLowerCase();
    let items: { label: string; insert: string; sub?: string }[] = [];
    if (kind === '@') items = [...this.deps.users().values()].filter((u) => u.name.toLowerCase().includes(q)).slice(0, 8).map((u) => ({ label: u.name, insert: `@${u.name} `, sub: this.deps.isOnline(u.id) ? 'online' : '' }));
    if (kind === '#') items = [...this.channels.values()].filter((c) => (c.kind === 'public' || c.kind === 'private') && c.name.includes(q)).slice(0, 8).map((c) => ({ label: `#${c.name}`, insert: `#${c.name} `, sub: c.topic }));
    if (kind === ':' && q.length >= 2) items = Object.entries(SHORTCODES).filter(([k]) => k.startsWith(q)).slice(0, 8).map(([k, v]) => ({ label: `${v} :${k}:`, insert: `${v} ` }));
    if (!items.length) { ac.classList.add('hidden'); return; }
    ac.dataset.token = m[1] + m[2];
    ac.innerHTML = items.map((it, i) => `<div class="ac-item${i === 0 ? ' sel' : ''}" data-insert="${esc(it.insert)}"><span>${esc(it.label)}</span><span class="small">${esc(it.sub ?? '')}</span></div>`).join('');
    const r = ta.getBoundingClientRect();
    ac.style.left = `${r.left}px`; ac.style.bottom = `${window.innerHeight - r.top + 6}px`; ac.style.width = `${Math.min(360, r.width)}px`;
    ac.classList.remove('hidden');
  }

  private acceptAc(ta: HTMLTextAreaElement, item: HTMLElement | undefined): void {
    const ac = document.getElementById('c-ac'); if (!ac || !item) return;
    const token = ac.dataset.token ?? '';
    const pos = ta.selectionStart ?? ta.value.length;
    const before = ta.value.slice(0, pos), after = ta.value.slice(pos);
    const cut = before.lastIndexOf(token);
    ta.value = before.slice(0, cut) + (item.dataset.insert ?? '') + after;
    const np = cut + (item.dataset.insert ?? '').length; ta.setSelectionRange(np, np);
    ac.classList.add('hidden'); ta.focus();
  }

  private async onClick(ev: MouseEvent): Promise<void> {
    const t = ev.target as HTMLElement;
    const chanEl = t.closest<HTMLElement>('[data-chan]');
    const actEl = t.closest<HTMLElement>('[data-act]');
    const viewEl = t.closest<HTMLElement>('[data-view]');
    const userEl = t.closest<HTMLElement>('.cname[data-user], .mention[data-user]');
    if (viewEl?.dataset.view) { await this.showList(viewEl.dataset.view as ListView); return; }
    if (actEl?.dataset.act) { await this.action(actEl.dataset.act, actEl, ev); return; }
    if (chanEl?.dataset.chan && (chanEl.classList.contains('citem') || chanEl.classList.contains('chanref'))) { await this.selectChannel(chanEl.dataset.chan); return; }
    if (userEl?.dataset.user && userEl.dataset.user !== this.deps.me.id) { await this.openDm([userEl.dataset.user]); return; }
  }

  private async action(act: string, el: HTMLElement, ev: MouseEvent): Promise<void> {
    const cm = el.closest<HTMLElement>('.cm'); const mid = cm?.dataset.id ?? '';
    const c = this.cur();
    switch (act) {
      case 'react': await this.react(mid, el.dataset.emoji ?? '👍'); break;
      case 'pick': this.emojiPicker(mid, ev); break;
      case 'thread': { const m = this.find(mid); await this.openThread(m?.threadId ?? mid); break; }
      case 'save': { const r = await this.api(`messages/${mid}/save`, 'POST'); if (r.ok) { const { on } = (await r.json()) as { on: boolean }; const m = this.find(mid); if (m) m.saved = on; this.renderMsgs(); this.renderPanel(); this.deps.toast(on ? 'Saved' : 'Removed from saved'); } break; }
      case 'pin': await this.api(`messages/${mid}/pin`, 'POST'); break;
      case 'link': { await navigator.clipboard?.writeText(`${location.origin}/#msg=${mid}`).catch(() => undefined); this.deps.toast('Link copied'); break; }
      case 'edit': this.editing = mid; this.renderMsgs(); this.renderPanel(); ($('.cedit-in') as HTMLTextAreaElement | null)?.focus(); break;
      case 'edit-cancel': this.editing = null; this.renderMsgs(); this.renderPanel(); break;
      case 'edit-save': await this.saveEdit(mid, ($('.cedit-in', cm!) as HTMLTextAreaElement).value); break;
      case 'del': if (confirm('Delete this message?')) await this.api(`messages/${mid}`, 'DELETE'); break;
      case 'more': await this.loadOlder(); break;
      case 'latest': { if (c) { await this.fetchMessages(c.id); this.renderMsgs(); this.scrollBottom(true); } break; }
      case 'mute': { if (c) { const r = await this.api(`channels/${c.id}/mute`, 'POST'); if (r.ok) { const { channel } = (await r.json()) as { channel: ChatChannel }; this.channels.set(c.id, { ...channel, unread: c.unread, mentions: c.mentions }); this.renderSide(); this.renderPanel(); } } break; }
      case 'files': { if (c) { const r = await this.api(`channels/${c.id}/files`); const files = r.ok ? ((await r.json()) as { files: { id: string; name: string; mime: string; size: number; url: string; userId: string; ts: number; messageId: string | null }[] }).files : []; this.modal(`Files in ${this.channelName(c)}`, `<div class="cm-users">${files.map((f) => `<a class="cm-user" href="${esc(f.url)}" target="_blank" rel="noopener">${f.mime.startsWith('image/') ? `<img class="fthumb" src="${esc(f.url)}" alt="">` : '📎'}<span class="cn">${esc(f.name)}</span><span class="small">${esc(this.nameOf(f.userId))} · ${fmtSize(f.size)}</span>${f.messageId ? `<button type="button" class="cplus" data-jump="${f.messageId}" title="Go to message">↗</button>` : ''}</a>`).join('') || '<div class="muted">No files shared here yet.</div>'}</div>`, 'Close', () => undefined); document.querySelectorAll<HTMLElement>('[data-jump]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); (document.getElementById('c-modal') as HTMLDialogElement).close(); void this.jumpTo(b.dataset.jump!); })); } break; }
      case 'close-panel': this.panel = null; this.renderPanel(); break;
      case 'thread-send': { const ta = $('#c-thread-in') as HTMLTextAreaElement; const text = ta.value; ta.value = ''; if (this.panel?.kind === 'thread') await this.send(text, this.panel.rootId); break; }
      case 'details': this.panel = this.panel?.kind === 'details' ? null : { kind: 'details' }; this.renderPanel(); break;
      case 'members': this.panel = { kind: 'details' }; this.renderPanel(); break;
      case 'pins': { if (!c) break; const r = await this.api(`channels/${c.id}/pins`); if (r.ok) { this.listItems = ((await r.json()) as { messages: ChatMessage[] }).messages; this.view = 'search'; this.renderAll(); $('#c-head .chtitle').textContent = `📌 Pinned in ${this.channelName(c)}`; } break; }
      case 'meet': { window.open('https://meet.google.com/new', '_blank', 'noopener'); if (c) await this.send('📹 started a Google Meet: https://meet.google.com/new'); break; }
      case 'topic': { if (!c) break; const v = prompt('Channel topic', c.topic); if (v !== null) await this.api(`channels/${c.id}`, 'PATCH', { topic: v }); break; }
      case 'purpose': { if (!c) break; const v = prompt('Channel purpose', c.purpose); if (v !== null) await this.api(`channels/${c.id}`, 'PATCH', { purpose: v }); break; }
      case 'rename': { if (!c) break; const v = prompt('New channel name', c.name); if (v) { const r = await this.api(`channels/${c.id}`, 'PATCH', { name: v }); if (!r.ok) this.deps.toast(((await r.json()) as { error: string }).error); } break; }
      case 'archive': { if (!c) break; await this.api(`channels/${c.id}`, 'PATCH', { archived: !c.archived }); break; }
      case 'leave': { if (c && confirm(`Leave ${this.channelName(c)}?`)) await this.leave(c.id); break; }
      case 'add-members': { if (!c) break; this.pickUsers('Add people', c.members, async (ids) => { await this.api(`channels/${c.id}/invite`, 'POST', { userIds: ids }); }); break; }
      case 'new-dm': this.pickUsers('New message', [this.deps.me.id], async (ids) => { if (ids.length) await this.openDm(ids); }); break;
      case 'new-channel': await this.channelDialog(); break;
      case 'webhooks': if (c) await this.webhookDialog(c); break;
    }
  }

  private async saveEdit(mid: string, text: string): Promise<void> {
    this.editing = null;
    if (!text.trim()) { this.renderMsgs(); return; }
    const r = await this.api(`messages/${mid}`, 'PATCH', { body: text });
    if (!r.ok) this.deps.toast('Could not edit');
    this.renderMsgs(); this.renderPanel();
  }

  private emojiPicker(mid: string, ev: MouseEvent): void {
    let pk = document.getElementById('c-emoji'); if (pk) pk.remove();
    pk = document.createElement('div'); pk.id = 'c-emoji';
    pk.innerHTML = QUICK_EMOJI.map((e) => `<button data-e="${e}">${e}</button>`).join('') + '<input placeholder="other…" maxlength="4">';
    pk.style.left = `${Math.min(window.innerWidth - 300, ev.clientX)}px`; pk.style.top = `${Math.min(window.innerHeight - 90, ev.clientY + 8)}px`;
    document.body.appendChild(pk);
    const done = (e: string) => { pk!.remove(); if (e) void this.react(mid, e); };
    pk.addEventListener('click', (e) => { const b = (e.target as HTMLElement).closest<HTMLElement>('[data-e]'); if (b) done(b.dataset.e!); });
    const inp = pk.querySelector('input')!; inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') done(inp.value.trim()); if (e.key === 'Escape') done(''); });
    setTimeout(() => document.addEventListener('mousedown', (e) => { if (!pk!.contains(e.target as Node)) pk!.remove(); }, { once: true }), 0);
  }

  // ---------- dialogs ----------

  private modal(title: string, bodyHtml: string, submitLabel: string, onSubmit: (root: HTMLElement) => Promise<boolean | void> | boolean | void): void {
    let dlg = document.getElementById('c-modal') as HTMLDialogElement | null;
    if (!dlg) { dlg = document.createElement('dialog'); dlg.id = 'c-modal'; document.body.appendChild(dlg); }
    dlg.innerHTML = `<form class="card compact" method="dialog"><h2>${esc(title)}</h2>${bodyHtml}<div class="row end"><button class="btn" type="button" data-x="cancel">Cancel</button><button class="btn primary" type="submit">${esc(submitLabel)}</button></div></form>`;
    const form = dlg.querySelector('form')!;
    form.querySelector('[data-x="cancel"]')!.addEventListener('click', () => dlg!.close());
    form.addEventListener('submit', async (e) => { e.preventDefault(); const ok = await onSubmit(form); if (ok !== false) dlg!.close(); });
    dlg.showModal();
    (form.querySelector('input, textarea') as HTMLElement | null)?.focus();
  }

  private pickUsers(title: string, exclude: string[], done: (ids: string[]) => Promise<void>): void {
    const users = [...this.deps.users().values()].filter((u) => !exclude.includes(u.id)).sort((a, b) => a.name.localeCompare(b.name));
    this.modal(title, `<input id="cm-filter" placeholder="Search people…" autocomplete="off"><div class="cm-users">${users.map((u) => `<label class="cm-user"><input type="checkbox" value="${u.id}"><span class="pres${this.deps.isOnline(u.id) ? ' on' : ''}"></span>${esc(u.name)}</label>`).join('') || '<div class="muted">No one else to add.</div>'}</div>`, 'Go', async (root) => {
      const ids = [...root.querySelectorAll<HTMLInputElement>('input[type=checkbox]:checked')].map((i) => i.value);
      await done(ids);
    });
    const f = document.getElementById('cm-filter') as HTMLInputElement | null;
    f?.addEventListener('input', () => { const q = f.value.toLowerCase(); document.querySelectorAll<HTMLElement>('.cm-user').forEach((el) => { el.style.display = el.textContent!.toLowerCase().includes(q) ? '' : 'none'; }); });
  }

  private async channelDialog(): Promise<void> {
    const r = await this.api('browse');
    const browse = r.ok ? ((await r.json()) as { channels: { id: string; name: string; purpose: string; memberCount: number; joined: boolean; archived: boolean }[] }).channels : [];
    const list = browse.filter((b) => !b.joined && !b.archived).map((b) => `<button type="button" class="citem" data-join="${b.id}"><span class="cic">#</span><span class="cn">${esc(b.name)}</span><span class="small">${b.memberCount} · ${esc(b.purpose)}</span></button>`).join('');
    this.modal('Channels', `<div class="cp-l">Browse</div><div class="cm-users">${list || '<div class="muted">You\'re in every public channel.</div>'}</div>
      <div class="cp-l">Create a channel</div><input id="cm-name" placeholder="e.g. marketing" maxlength="40" autocomplete="off"><input id="cm-purpose" placeholder="What's it for? (optional)" maxlength="250" autocomplete="off">
      <label class="row"><input type="checkbox" id="cm-private"> Private — only invited people can see it</label>`, 'Create', async (root) => {
      const name = ($('#cm-name', root) as HTMLInputElement).value.trim(); if (!name) return false;
      const res = await this.api('channels', 'POST', { name, purpose: ($('#cm-purpose', root) as HTMLInputElement).value, kind: ($('#cm-private', root) as HTMLInputElement).checked ? 'private' : 'public' });
      if (!res.ok) { this.deps.toast(((await res.json()) as { error: string }).error); return false; }
      const { channel } = (await res.json()) as { channel: ChatChannel };
      this.channels.set(channel.id, channel); await this.selectChannel(channel.id);
    });
    document.querySelectorAll<HTMLElement>('[data-join]').forEach((b) => b.addEventListener('click', async () => {
      const res = await this.api(`channels/${b.dataset.join}/join`, 'POST');
      if (res.ok) { const { channel } = (await res.json()) as { channel: ChatChannel }; this.channels.set(channel.id, channel); (document.getElementById('c-modal') as HTMLDialogElement).close(); await this.selectChannel(channel.id); }
    }));
  }

  private async webhookDialog(c: ChatChannel): Promise<void> {
    const r = await this.api(`channels/${c.id}/webhooks`);
    const hooks = r.ok ? ((await r.json()) as { webhooks: { id: string; name: string; url: string; createdBy: string }[] }).webhooks : [];
    this.modal('Incoming webhooks', `<div class="small">POST JSON like <code>{"text":"Hello from CI"}</code> (Slack-compatible: <code>username</code>, <code>icon_emoji</code> work too).</div>
      <div class="cm-users">${hooks.map((h) => `<div class="cm-user col"><b>${esc(h.name)}</b><input readonly value="${esc(h.url)}" onclick="this.select()"><button type="button" class="btn small-btn danger" data-hook-del="${h.id}">Revoke</button></div>`).join('') || '<div class="muted">No webhooks yet.</div>'}</div>
      <input id="cm-hook" placeholder="New webhook name, e.g. GitHub" maxlength="40" autocomplete="off">`, 'Create webhook', async (root) => {
      const name = ($('#cm-hook', root) as HTMLInputElement).value.trim(); if (!name) return false;
      const res = await this.api(`channels/${c.id}/webhooks`, 'POST', { name });
      if (!res.ok) { this.deps.toast('Could not create'); return false; }
      await this.webhookDialog(c); return false;
    });
    document.querySelectorAll<HTMLElement>('[data-hook-del]').forEach((b) => b.addEventListener('click', async () => { await this.api(`webhooks/${b.dataset.hookDel}`, 'DELETE'); await this.webhookDialog(c); }));
  }
}

function dedupe(list: ChatMessage[]): ChatMessage[] { const seen = new Set<string>(); return list.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true))).sort((a, b) => a.ts - b.ts); }
function upsert(list: ChatMessage[], m: ChatMessage): ChatMessage[] { const i = list.findIndex((x) => x.id === m.id); if (i >= 0) { list[i] = { ...list[i], ...m }; return list; } list.push(m); return list.sort((a, b) => a.ts - b.ts); }
function autoGrow(ta: HTMLTextAreaElement): void { ta.style.height = 'auto'; ta.style.height = `${Math.min(200, ta.scrollHeight)}px`; }
/** a soft two-tone chime for DMs / mentions */
function beep(): void {
  try {
    const ac = new AudioContext(); const t = ac.currentTime;
    for (const [f, at] of [[880, 0], [1175, 0.12]] as const) { const o = ac.createOscillator(), g = ac.createGain(); o.type = 'sine'; o.frequency.value = f; g.gain.setValueAtTime(0.0001, t + at); g.gain.exponentialRampToValueAtTime(0.08, t + at + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t + at + 0.25); o.connect(g).connect(ac.destination); o.start(t + at); o.stop(t + at + 0.3); }
    setTimeout(() => void ac.close(), 800);
  } catch { /* no audio */ }
}
function fmtSize(n: number): string { return n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`; }
